import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context.js';
import { ThemeProvider } from './lib/theme.js';
import { LoginPage } from './pages/LoginPage.js';
import { AppShell, ModuleRoute, AppIndexRedirect } from './pages/AppShell.js';
import { ChangePasswordPage } from './pages/ChangePasswordPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { SsoPage } from './pages/SsoPage.js';
import { LoggedOutPage } from './pages/LoggedOutPage.js';

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword && window.location.pathname !== '/must-change-password')
    return <Navigate to="/must-change-password" replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function FullPageLoader() {
  return (
    <div className="nx-loader" role="status" aria-live="polite">
      Carregando…
    </div>
  );
}

function ForcedChangePassword() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.mustChangePassword) return <Navigate to="/app" replace />;
  return <ChangePasswordPage forced />;
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicOnly>
                  <LoginPage />
                </PublicOnly>
              }
            />
            <Route
              path="/must-change-password"
              element={<ForcedChangePassword />}
            />
            <Route
              path="/forgot-password"
              element={
                <PublicOnly>
                  <ForgotPasswordPage />
                </PublicOnly>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicOnly>
                  <ResetPasswordPage />
                </PublicOnly>
              }
            />
            <Route path="/sso" element={<SsoPage />} />
            <Route path="/logged-out" element={<LoggedOutPage />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<AppIndexRedirect />} />
              <Route path="chargers/:cpId" element={<ModuleRoute chargerDetail />} />
              <Route path=":slug" element={<ModuleRoute />} />
            </Route>
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
